import { motion } from "framer-motion";
import { Check, Star } from "lucide-react";
import { PRICING } from "@/data/content";
import { SectionHeader, CTAButton } from "@/components/ui-bits";
import { staggerParent, staggerChild } from "@/components/motion-primitives";
import { cn } from "@/lib/utils";

const Card = ({ p }) => {
  const hl = p.highlighted;
  return (
    <motion.div
      variants={staggerChild}
      data-testid={`pricing-card-${p.tier.toLowerCase().replace(/\s+/g, "-")}`}
      className={cn(
        "relative flex flex-col rounded-2xl p-8 md:p-9 transition-all duration-300",
        hl
          ? "bg-brand-dark grain text-white shadow-[0_40px_90px_-30px_rgba(15,23,42,0.6)] lg:scale-[1.05] lg:-translate-y-2 border border-secondary/30 z-10"
          : "bg-white border border-slate-200/80 shadow-[0_2px_10px_rgba(15,23,42,0.05)] hover:shadow-lg hover:-translate-y-1"
      )}
    >
      {hl && (
        <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-secondary text-white text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full shadow">
          <Star className="w-3.5 h-3.5 fill-white" /> {p.badge}
        </span>
      )}
      <div className={cn("overline", hl ? "text-secondary" : "text-secondary")}>{p.tier}</div>
      <h3 className={cn("mt-3 font-heading text-xl font-semibold leading-snug", hl ? "text-white" : "text-primary")}>{p.name}</h3>

      <div className="mt-5 flex items-end gap-1">
        <span className={cn("font-heading text-4xl md:text-5xl font-semibold tracking-tight", hl ? "text-white" : "text-primary")}>{p.price}</span>
        <span className={cn("mb-1.5 text-sm", hl ? "text-white/60" : "text-muted-foreground")}>{p.period}</span>
      </div>

      <p className={cn("mt-4 text-sm", hl ? "text-white/70" : "text-muted-foreground")}>{p.intro}</p>

      <ul className="mt-5 space-y-2.5 flex-1">
        {p.features.map((f, i) => (
          <li key={i} className={cn("flex items-start gap-2.5 text-sm", hl ? "text-white/85" : "text-slate-700")}>
            <span className={cn("mt-0.5 flex items-center justify-center w-5 h-5 rounded-full shrink-0", hl ? "bg-secondary text-white" : "bg-secondary/15 text-secondary")}>
              <Check className="w-3 h-3" />
            </span>
            {f}
          </li>
        ))}
      </ul>

      <div className="mt-8">
        <CTAButton
          href="#quote"
          variant={hl ? "secondary" : "outlineDark"}
          testId={`pricing-cta-${p.tier.toLowerCase().replace(/\s+/g, "-")}`}
          className="w-full"
        >
          {p.cta}
        </CTAButton>
      </div>
    </motion.div>
  );
};

export const Pricing = () => (
  <section id="pricing" className="relative py-24 md:py-32 bg-background">
    <div className="container mx-auto px-4 md:px-8">
      <SectionHeader
        center
        overline="Service Packages"
        title="Monthly municipal account management packages"
        subtitle="Choose the level of ongoing monitoring that suits your property. All packages are billed monthly."
      />
      <motion.div
        variants={staggerParent} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
        className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-6 items-center max-w-6xl mx-auto"
      >
        {PRICING.map((p) => <Card key={p.tier} p={p} />)}
      </motion.div>
      <p className="mt-8 text-center text-xs text-muted-foreground max-w-2xl mx-auto">
        SML assists, investigates, manages, facilitates and follows up on municipal matters. Outcomes and municipal
        turnaround times are not guaranteed.
      </p>
    </div>
  </section>
);
